import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { toast } from "sonner";
import {
  Upload,
  FileText,
  Image as ImageIcon,
  FileAudio,
  FileVideo,
  File as FileIcon,
  Download,
  Trash2,
  Loader2,
  FolderOpen,
} from "lucide-react";
import { filesApi } from "../lib/api";

const MAX_BYTES = 250 * 1024 * 1024;

const humanSize = (n) => {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

const iconFor = (ctype = "", name = "") => {
  const c = ctype.toLowerCase();
  if (c.startsWith("image/")) return ImageIcon;
  if (c.startsWith("audio/")) return FileAudio;
  if (c.startsWith("video/")) return FileVideo;
  if (
    c === "application/pdf" ||
    c.startsWith("text/") ||
    /\.(md|txt|json|csv|log|xml|html?)$/i.test(name)
  )
    return FileText;
  return FileIcon;
};

const humanDate = (iso) => {
  try {
    return new Date(iso).toLocaleString();
  } catch (_e) {
    return "";
  }
};

export const FilesPanel = ({ open, onOpenChange, activeSessionId }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [scope, setScope] = useState("all"); // "all" | "session"
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await filesApi.list(
        scope === "session" ? activeSessionId : undefined
      );
      setItems(res.files || []);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Couldn't load files"
      );
    } finally {
      setLoading(false);
    }
  }, [scope, activeSessionId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const doUpload = async (fileList) => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    for (const f of chosen) {
      if (f.size > MAX_BYTES) {
        toast.error(
          `"${f.name}" is ${humanSize(f.size)} — max 250 MB per file`
        );
        continue;
      }
      setUploading(true);
      setProgress(0);
      try {
        // Only tag with session when the user is currently viewing that scope,
        // otherwise it goes into the general library.
        const sid = scope === "session" ? activeSessionId : undefined;
        const res = await filesApi.upload(f, sid, setProgress);
        setItems((prev) => [res.file, ...prev]);
        toast.success(`Uploaded ${f.name}`);
      } catch (e) {
        toast.error(
          e?.response?.data?.detail || e.message || `Upload of ${f.name} failed`
        );
      } finally {
        setUploading(false);
        setProgress(0);
      }
    }
  };

  const onPick = (e) => {
    doUpload(e.target.files);
    // Reset so picking the same file twice still fires change
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    doUpload(e.dataTransfer.files);
  };

  const download = async (f) => {
    try {
      const blobUrl = await filesApi.fetchBlobUrl(f.id);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = f.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Download failed"
      );
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.filename}?`)) return;
    try {
      await filesApi.remove(f.id);
      setItems((prev) => prev.filter((x) => x.id !== f.id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || e.message || "Delete failed"
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="files-panel"
        className="glass-strong border-white/10 w-[95vw] max-w-3xl max-h-[92vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 grid-cols-[minmax(0,1fr)]"
      >
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-blue-300" /> Files
          </DialogTitle>
          <p className="text-xs text-white/50 -mt-1">
            Your personal library — documents, audio, images, video (up to 250 MB each).
          </p>
        </DialogHeader>

        {/* Scope tabs */}
        {activeSessionId && (
          <div className="flex items-center gap-1 mt-3 p-0.5 rounded-lg bg-white/[0.04] border border-white/10 w-fit">
            {[
              { key: "all", label: "All files" },
              { key: "session", label: "This chat only" },
            ].map((s) => (
              <button
                key={s.key}
                type="button"
                data-testid={`files-scope-${s.key}`}
                onClick={() => setScope(s.key)}
                className={`px-3 py-1 text-xs rounded-md transition-colors ${
                  scope === s.key
                    ? "bg-blue-500/20 text-blue-100"
                    : "text-white/60 hover:text-white hover:bg-white/5"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}

        {/* Upload drop-zone */}
        <div
          data-testid="files-dropzone"
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => !uploading && fileInputRef.current?.click()}
          className={`mt-4 rounded-xl border-2 border-dashed transition-colors cursor-pointer p-6 text-center ${
            dragOver
              ? "border-blue-400/60 bg-blue-500/10"
              : "border-white/15 bg-white/[0.02] hover:bg-white/[0.04]"
          } ${uploading ? "pointer-events-none opacity-70" : ""}`}
        >
          <input
            ref={fileInputRef}
            data-testid="files-input"
            type="file"
            multiple
            className="hidden"
            onChange={onPick}
          />
          <Upload className="h-6 w-6 text-blue-300 mx-auto mb-2" />
          {uploading ? (
            <div className="text-sm text-white/80">
              Uploading… {progress}%
              <div className="mt-2 mx-auto max-w-xs h-1.5 rounded-full bg-white/10 overflow-hidden">
                <div
                  className="h-full bg-blue-400 transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          ) : (
            <>
              <div className="text-sm text-white/90">
                Drop files here or click to browse
              </div>
              <div className="text-[11px] text-white/45 mt-1">
                {scope === "session"
                  ? "Attaches to the current chat session"
                  : "Saved to your general library"}
              </div>
            </>
          )}
        </div>

        {/* List */}
        <div className="mt-4 space-y-1.5">
          <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">
            {loading
              ? "Loading…"
              : items.length
              ? `${items.length} file${items.length === 1 ? "" : "s"}`
              : "No files yet"}
          </div>
          {items.map((f) => {
            const Icon = iconFor(f.content_type, f.filename);
            return (
              <div
                key={f.id}
                data-testid={`file-row-${f.id}`}
                className="group flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-3 py-2 transition-colors"
              >
                <Icon className="h-5 w-5 text-white/70 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white/90 truncate">
                    {f.filename}
                  </div>
                  <div className="text-[11px] text-white/45 flex items-center gap-2">
                    <span>{humanSize(f.size)}</span>
                    <span>·</span>
                    <span>{humanDate(f.created_at)}</span>
                    {f.session_id && (
                      <>
                        <span>·</span>
                        <span className="text-blue-300/70">attached to chat</span>
                      </>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid={`file-download-${f.id}`}
                  onClick={() => download(f)}
                  className="opacity-60 hover:opacity-100 h-8 w-8"
                  aria-label="Download"
                  title="Download"
                >
                  <Download className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid={`file-delete-${f.id}`}
                  onClick={() => remove(f)}
                  className="opacity-60 hover:opacity-100 h-8 w-8 hover:text-red-300"
                  aria-label="Delete"
                  title="Delete"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
